import React, { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import CO2Service from '~/api/services/co2'
import FarmerGroupsService from '~/api/services/farmerGroups'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { useAuth } from '~/routes/components/AuthProvider'
import { StyledMenuItem } from '../CloudStorage/style'
import dayjs from 'dayjs'
import { getDateWithTime, getDateWithoutTime, getMinDate, handleErrors, parseDate } from '~/utils/helpers'
import { RouteTypes } from '~/routes/constants'
import { Form, FormGroup, Segment, Tab, TabPane } from 'semantic-ui-react'
import { FormFields } from '~/components/FormFields'
import { getError } from '~/components/FormFields/util'
import { DateInput, DateTimeInput } from 'semantic-ui-calendar-react'
import { StyledErrorLabel } from '../MilkMoney/MilkDelivery/ShowTours/style'
import { CO2_LFL_SLUG } from '~/Constants'
import LanguageTab from './LanguageTab'

const dateFields = [
  {
    key: 'start',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    key: 'end',
    format: 'DD.MM.YYYY HH:mm'
  },
  {
    key: 'periodFrom',
    format: 'DD.MM.YYYY'
  },
  {
    key: 'periodTo',
    format: 'DD.MM.YYYY'
  }
]

const CreateEditCo2Climate: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const [isBusy, setIsBusy] = useState(false)
  const { systemData: { languages, logged: { languageId } } } = useAuth()
  const languageLocal = languages.find(language => language.id === languageId)
  const [activeIndex, setActiveIndex] = useState<string | number>(0)
  const [options, setOptions] = useState(null)
  const navigate = useNavigate()
  const { handleSubmit, control, reset, getValues, setValue, watch, formState, formState: { isSubmitted } } = useForm({
    mode: 'onSubmit'
  })
  const watchPeriodFrom = watch('periodFrom')
  const watchPeriodTo = watch('periodTo')
  const watchStartDate = watch('start')
  const watchEndDate = watch('end')

  useEffect(() => {
    fetchCO2ClimateData()
  }, [])

  useEffect(() => {
    if (watchPeriodFrom && watchPeriodTo) {
      const periodFrom = getDateWithoutTime(watchPeriodFrom)
      const periodTo = getDateWithoutTime(watchPeriodTo)
      if (dayjs(periodTo).diff(dayjs(periodFrom)) <= 0) {
        setValue('periodTo', getDayAhead(watchPeriodTo))
      }
    }
  }, [watchPeriodFrom])

  useEffect(() => {
    if (formState.isSubmitted) {
      if (Object.keys(formState.errors).length > 0) {
        notify(t('Please fill in all required fields'), 'error')
      }
    }

  }, [formState.isSubmitted])

  function getMenuItem(pane, index) {
    const languagesErrors = formState.errors?.languages;
    const paneHasError = index in (languagesErrors || {});

    return <StyledMenuItem
      key={pane}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{pane}</div>
    </StyledMenuItem>
  }

  async function fetchCO2ClimateData() {
    setIsBusy(true);
    try {
      const responseOptions = await CO2Service.getCO2OptionsData()
      const responseFarmersList = await FarmerGroupsService.getFarmerGroupsList(CO2_LFL_SLUG)
      setOptions({ ...responseOptions.data, farmersList: responseFarmersList.data.map(farmer => ({ value: farmer.id, text: farmer.name })) })
      const { languages } = responseOptions.data
      if (id) {
        const response = await CO2Service.getCO2Footprint(id)
        const languagesEdit = response.data.languages
        languages.map((language) => {
          const indexLang = languagesEdit.findIndex(languageEd => languageEd.id === language.id)
          if (indexLang < 0) {
            languagesEdit.push({
              id: language.id,
              title: '',
              description: ''
            })
          } else {
            languagesEdit[indexLang].order = language.order
          }
        })
        languagesEdit.sort((a, b) => a.order - b.order)
        languagesEdit.forEach(lang => delete lang.order)
        response.data.languages = languagesEdit
        dateFields.forEach(dateField => response.data[dateField.key] = dayjs(new Date(response.data[dateField.key])).format(dateField.format))
        reset(response.data)
      } else {
        const languagesNew = []
        languages.map((language) => {
          languagesNew.push({
            id: language.id,
            title: '',
            description: ''
          })
        })
        setValue('languages', languagesNew)
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function convertDate(date) {
    const [day, month, yearAndTime] = date.split('.')
    const year = yearAndTime?.split(' ')[0]
    return (`${year}-${month}-${day}`)
  }

  function convertDateWithTime(date) {
    const [day, month, yearAndTime] = date.split('.')
    const year = yearAndTime?.split(' ')[0]
    const time = yearAndTime?.split(' ')[1]
    return (`${year}-${month}-${day} ${time}`)
  }

  function removeIfNotBase64(languageArray) {
    languageArray.forEach(language => {
      if (!language.file) {
        language.filename = null
        language.originalFilename = null
      }
    })
    return languageArray
  }

  async function onSubmit(data) {
    setIsBusy(true)
    data.periodFrom = convertDate(watchPeriodFrom)
    data.periodTo = convertDate(watchPeriodTo)
    data.dateFrom = convertDateWithTime(watchStartDate)
    data.dateTo = convertDateWithTime(watchEndDate)
    //remove attachment if it is not base64 (api issue)
    data.languages = removeIfNotBase64(data.languages)
    try {
      if (id) {
        await CO2Service.updateCO2Footprint(id, data)
        notify(t('You have successfully updated the data collection for the CO2 GHG emissions calculation'), 'success')
        navigate(RouteTypes.CO2_CLIMATE.replace(':id', id))
      } else {
        const response = await CO2Service.createCO2Footprint(data)
        notify(t('You have successfully created the data collection for the CO2 GHG emissions calculation'), 'success')
        navigate(RouteTypes.CO2_CLIMATE.replace(':id', response.data.surveyId))
      }
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderLanguagesForm() {
    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        renderActiveOnly={false}
        onTabChange={(_, data) => setActiveIndex(data.activeIndex)}
        panes={options?.languages?.map((language, languageIndex) => {
          const langIndex = getValues('languages').findIndex(langValue => langValue.id === language.id)
          return {
            menuItem: getMenuItem(language.label, langIndex),
            pane: <TabPane
              active={languageIndex === activeIndex}
              style={{ border: 'none' }}
              key={language?.id}
            >
              <LanguageTab
                index={langIndex}
                language={language}
                control={control}
                getValues={getValues}
                setValue={setValue}
                languages={getValues('languages')}
              />
            </TabPane>
          };
        })}
      />
    );
  }

  const handleKeyPress = (event) => {
    event.preventDefault();
  }

  function getDayAhead(date) {
    const inputDate = parseDate(date)
    const parsedDate = dayjs(inputDate, { format: 'DD.MM.YYYY' });
    if (parsedDate.isValid()) {
      const previousDay = parsedDate.add(1, 'day');
      return previousDay.format('DD.MM.YYYY');
    }
    return parsedDate.format('DD.MM.YYYY')
  }

  function getDayBehind(date, numberBehind = 1) {
    const inputDate = parseDate(date)
    const parsedDate = dayjs(inputDate, { format: 'DD.MM.YYYY' });
    if (parsedDate.isValid()) {
      const previousDay = parsedDate.subtract(numberBehind, 'day');
      return previousDay.format('DD.MM.YYYY');
    }
    return parsedDate.format('DD.MM.YYYY')
  }

  function renderForm() {
    const todaysData = dayjs(new Date()).format('DD.MM.YYYY')
    return <>
      <FormGroup widths={'equal'}>
        <FormFields.Select
          control={control}
          name={'type'}
          upward
          rules={{
            required: {
              value: true,
              message: t('Please select type')
            }
          }}
          placeholder={t('Please select type')}
          label={<label dangerouslySetInnerHTML={{ __html: `${t('CO2 calculation logic')}<span style="color: red";>*</span>` }} />}
          options={options?.types.map(type => ({ text: type.label, value: type.key })) || []}
        />
        <FormFields.Select
          control={control}
          name={'farmerGroups'}
          upward
          rules={{
            required: {
              value: true,
              message: t('Please select farmer groups')
            }
          }}
          multiple
          placeholder={t('Please select farmer group')}
          label={t('Farmer groups')}
          options={options?.farmersList || []}
        />
      </FormGroup>
      <Form.Group widths={'equal'}>
        <Controller
          control={control}
          name={'periodFrom'}
          rules={{
            required: {
              value: true,
              message: t('Please enter period from')
            }
          }}
          render={({ field, fieldState }) => {
            return <Form.Field
              required
            >
              <label>{t('Period from')}</label>
              <DateInput
                name='date'
                placeholder={t('Period from')}
                maxDate={getDayBehind(todaysData, 2)}
                initialDate={getDayBehind(todaysData, 2)}
                closable
                onKeyDown={handleKeyPress}
                markColor={'blue'}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateFormat={'DD.MM.YYYY'}
                className={'datePicker'}
                value={watchPeriodFrom}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('periodFrom', value)}
              />
            </Form.Field>

          }}
        />
        <Controller
          control={control}
          name={'periodTo'}
          rules={{
            required: {
              value: true,
              message: t('Please enter period to')
            }
          }}
          render={({ field, fieldState }) => {
            const dateFrom = getDateWithoutTime(watchPeriodFrom || '')
            const dateTo = getDateWithoutTime(watchPeriodTo || '')
            const todaysDate = dayjs(new Date()).format('DD.MM.YYYY')
            return <Form.Field
              required
              error={!watchPeriodTo && isSubmitted ? getError(fieldState) : false}
            >
              <label>{t('Period to')}</label>
              <DateInput
                name='date'
                className={'datePicker'}
                placeholder={t('Period to')}
                closable
                markColor={'blue'}
                onKeyDown={handleKeyPress}
                onKeyPress={handleKeyPress}
                initialDate={getDayAhead(watchPeriodFrom || todaysDate)}
                minDate={getDayAhead(watchPeriodFrom || todaysDate)}
                maxDate={getDayBehind(todaysData, 1)}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateFormat={'DD.MM.YYYY'}
                disabled={!watchPeriodFrom}
                value={watchPeriodTo}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('periodTo', value)}
              />
              {!watchPeriodTo && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter period to')}</StyledErrorLabel>}
              {dateFrom && dateTo && dayjs(dateTo).diff(dayjs(dateFrom)) < 0 &&
                <StyledErrorLabel className='ui pointing basic label'>{t('Please select a date ahead of the period from.')}</StyledErrorLabel>}
            </Form.Field>

          }}
        />
      </Form.Group >
      <Form.Group widths={'equal'}>
        <Controller
          control={control}
          name={'start'}
          rules={{
            required: {
              value: true,
              message: t('Please enter start date')
            },
            validate: () => {
              const dateFrom = getDateWithTime(watchStartDate)
              const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
              if (dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0) {
                return t('Please select a date ahead of the current date.')
              }
              return true
            }
          }}
          render={({ field, fieldState }) => {
            const dateFrom = watchStartDate ? getDateWithTime(watchStartDate) : ''
            const currentDate = getDateWithTime(dayjs(new Date()).format('DD.MM.YYYY HH:mm'))
            return <Form.Field
              required
              error={!watchStartDate && isSubmitted ? getError(fieldState) : dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 ? true : false}
            >
              <label>{t('Start of data collection')}</label>
              <DateTimeInput
                name='dateTime'
                placeholder={t('Start of data collection')}
                minDate={dayjs(new Date()).format('DD.MM.YYYY HH:mm')}
                closable
                onKeyDown={handleKeyPress}
                markColor={'blue'}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateTimeFormat={'DD.MM.YYYY HH:mm'}
                className={'datePicker'}
                value={watchStartDate}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('start', value)}
              />
              {!watchStartDate && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter start date')}</StyledErrorLabel>}
              {dateFrom && currentDate && dayjs(dateFrom).diff(dayjs(currentDate)) <= 0 &&
                <StyledErrorLabel className='ui pointing basic label'>{t('Please select a date ahead of the current date.')}</StyledErrorLabel>}
            </Form.Field>

          }}
        />
        <Controller
          control={control}
          name={'end'}
          rules={{
            required: {
              value: true,
              message: t('Please enter end date')
            },
            validate: () => {
              const dateFrom = getDateWithTime(watchStartDate)
              const dateTo = getDateWithTime(watchEndDate)
              if (dateFrom && dateTo && dayjs(dateTo).diff(dayjs(dateFrom)) <= 0) {
                return t('Please select a date ahead of the start date.')
              }
              return true
            }
          }}
          render={({ field, fieldState }) => {
            const dateFrom = getDateWithTime(watchStartDate || '')
            const dateTo = getDateWithTime(watchEndDate || '')
            return <Form.Field
              required
              error={!watchEndDate && isSubmitted ? getError(fieldState) : false}
            >
              <label>{t('End of data collection')}</label>
              <DateTimeInput
                name='dateTime'
                className={'datePicker'}
                placeholder={t('End of data collection')}
                closable
                markColor={'blue'}
                onKeyDown={handleKeyPress}
                onKeyPress={handleKeyPress}
                initialDate={getMinDate(watchStartDate)}
                minDate={getMinDate(watchStartDate)}
                localization={languageLocal.code}
                closeOnMouseLeave={false}
                dateTimeFormat={'DD.MM.YYYY HH:mm'}
                disabled={!watchStartDate}
                value={watchEndDate}
                iconPosition='left'
                onChange={(event, { name, value }) => setValue('end', value)}
              />
              {!watchEndDate && isSubmitted && <StyledErrorLabel className='ui pointing basic label'>{t('Please enter end date')}</StyledErrorLabel>}
              {dateFrom && dateTo && dayjs(dateTo).diff(dayjs(dateFrom)) <= 0 &&
                <StyledErrorLabel className='ui pointing basic label'>{t('Please select a date ahead of the start date.')}</StyledErrorLabel>}
            </Form.Field>

          }}
        />
      </Form.Group >
    </>
  }

  const title = id ? t('Update CO2 climate check') : t('Creation of CO2 data collection')

  return <Layout
    title={<span dangerouslySetInnerHTML={{ __html: title }} />}
    isBusy={isBusy}
    onSubmit={handleSubmit(onSubmit)}
    action={id ? t('Update') : t('Save')}
    segmented
    close={RouteTypes.CO2_CLIMATE}
    isCo2Loader={true}
  >
    {options && !isBusy && <>
      <Segment>
        {renderLanguagesForm()}
      </Segment>
      <Segment style={{ marginTop: '3em' }}>
        {renderForm()}
      </Segment>
    </>
    }
  </Layout>
}
export default CreateEditCo2Climate